import { legacy } from '@/shared/api/client';
import {
  approveCampaign,
  deleteCampaign,
  getCampaign,
  listAudiences,
  listCampaigns,
  mapAudienceOption,
  marketingKeys,
  passbookHeaders,
  runCampaignAction,
  unwrap,
  type ApiCampaignAction,
  type ApiEnvelope,
  type AudienceSegment,
} from '@/features/marketing-shared';
import type { CampaignDraftInput, EmailCampaign, EmailTemplate, SendingIdentity, TemplateDraft } from '../model/types';
import {
  mapEmailCampaign,
  mapEmailTemplate,
  mapSendingIdentity,
  toEmailApproveBody,
  toTemplateSaveBody,
  type ApiEmailCampaign,
  type ApiEmailTemplate,
  type ApiSendingIdentity,
} from './email-mappers';

/**
 * `/v1/email-marketing/*` do fixe-api, via `/bff/api`. Toda função recebe a
 * loja e manda `X-Passbook-Id`: o BFF confere a sessão e a API confere papel,
 * loja e a flag `hasEmailMarketing`.
 */

const CHANNEL = 'email' as const;
const BASE = '/email-marketing';

export const emailMarketingKeys = {
  campaigns: (idPassbook: string) => marketingKeys.campaigns(CHANNEL, idPassbook),
  campaignAll: (idPassbook: string, id: string) => marketingKeys.campaignAll(CHANNEL, idPassbook, id),
  campaign: (idPassbook: string, id: string, page: number) => marketingKeys.campaign(CHANNEL, idPassbook, id, page),
  audiences: (idPassbook: string) => marketingKeys.audiences(CHANNEL, idPassbook),
  templates: (idPassbook: string) => marketingKeys.templates(idPassbook),
  identities: (idPassbook: string) => marketingKeys.identities(idPassbook),
};

export async function fetchEmailCampaigns(idPassbook: string): Promise<EmailCampaign[]> {
  const rows = await listCampaigns<ApiEmailCampaign>(CHANNEL, idPassbook);
  return rows.map(mapEmailCampaign);
}

export async function fetchEmailCampaign(
  id: string,
  idPassbook: string,
  paging: { page: number; pageSize: number } = { page: 1, pageSize: 50 },
): Promise<EmailCampaign> {
  return mapEmailCampaign(await getCampaign<ApiEmailCampaign>(CHANNEL, id, idPassbook, paging));
}

export async function approveEmailCampaign(
  idPassbook: string,
  input: CampaignDraftInput,
  allowPartial: boolean,
): Promise<{ campaign: EmailCampaign; leftOut: number }> {
  const result = await approveCampaign<ApiEmailCampaign>(CHANNEL, idPassbook, toEmailApproveBody(input, allowPartial));
  return { campaign: mapEmailCampaign(result.campaign), leftOut: result.leftOut };
}

export async function emailCampaignAction(action: ApiCampaignAction, id: string, idPassbook: string): Promise<EmailCampaign> {
  return mapEmailCampaign(await runCampaignAction<ApiEmailCampaign>(CHANNEL, id, action, idPassbook));
}

export async function deleteEmailCampaign(id: string, idPassbook: string): Promise<void> {
  await deleteCampaign(CHANNEL, id, idPassbook);
}

export async function fetchEmailAudiences(idPassbook: string): Promise<AudienceSegment[]> {
  const rows = await listAudiences(CHANNEL, idPassbook);
  return rows.map(mapAudienceOption);
}

export async function fetchEmailTemplates(idPassbook: string): Promise<EmailTemplate[]> {
  const res = await legacy.get<ApiEnvelope<ApiEmailTemplate[]>>(
    `${BASE}/${encodeURIComponent(idPassbook)}/templates`,
    passbookHeaders(idPassbook),
  );
  const rows = unwrap(res);
  return Array.isArray(rows) ? rows.map(mapEmailTemplate) : [];
}

export async function fetchEmailTemplate(id: string, idPassbook: string): Promise<EmailTemplate> {
  const res = await legacy.get<ApiEnvelope<ApiEmailTemplate>>(
    `${BASE}/templates/${encodeURIComponent(id)}`,
    passbookHeaders(idPassbook),
  );
  return mapEmailTemplate(unwrap(res));
}

/** `PUT /templates`: modelo `local` é atualizado; `sandbox` vira cópia `local` (`forked`). */
export async function saveEmailTemplate(
  idPassbook: string,
  draft: TemplateDraft,
): Promise<{ template: EmailTemplate; forked: boolean }> {
  const res = await legacy.put<ApiEnvelope<{ template: ApiEmailTemplate; forked?: boolean }>>(
    `${BASE}/${encodeURIComponent(idPassbook)}/templates`,
    toTemplateSaveBody(draft),
    passbookHeaders(idPassbook),
  );
  const data = unwrap(res);
  return { template: mapEmailTemplate(data.template), forked: data.forked === true };
}

export async function duplicateEmailTemplate(id: string, idPassbook: string): Promise<EmailTemplate> {
  const res = await legacy.post<ApiEnvelope<{ template: ApiEmailTemplate }>>(
    `${BASE}/templates/${encodeURIComponent(id)}/duplicate`,
    undefined,
    passbookHeaders(idPassbook),
  );
  return mapEmailTemplate(unwrap(res).template);
}

/** Identidades que o master liberou para a loja, com o estado lido no provedor. */
export async function fetchSendingIdentities(idPassbook: string): Promise<SendingIdentity[]> {
  const res = await legacy.get<ApiEnvelope<ApiSendingIdentity[]>>(
    `${BASE}/${encodeURIComponent(idPassbook)}/identities`,
    passbookHeaders(idPassbook),
  );
  const rows = unwrap(res);
  return Array.isArray(rows) ? rows.map(mapSendingIdentity) : [];
}
